import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getOrders, updateOrderStatus, updateOrder, getRestaurant } from '../services/api';
import websocket from '../services/websocket';
import { KanbanBoard } from '../components/kanban';
import OrderDetailModal from '../components/kanban/OrderDetailModal';
import EditOrderModal from '../components/kanban/EditOrderModal';
import ConfirmActionModal from '../components/waiter/ConfirmActionModal';
import TableGrid from '../components/waiter/TableGrid';
import TableDetailPanel from '../components/waiter/TableDetailPanel';
import Header from '../components/Header';
import useStatusSounds from '../hooks/useStatusSounds';
import { useViewMode } from '../hooks/useViewMode';
import { useUserRole } from '../hooks/useUserRole';
import { useTables } from '../hooks/useTables';
import { useWaiterNotifications } from '../hooks/useWaiterNotifications';
import { useToast } from '../context/ToastContext';
import { trackEvent } from '../utils/analytics';
import { storage } from '../utils/storage';
import './OrdersPage.css';

function OrdersPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { role, isAdmin } = useUserRole();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllAreas, setShowAllAreas] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(
    () => storage.get('selectedRestaurantId')
  );
  const [activeOrder, setActiveOrder] = useState(null);
  const [selectedOrderForModal, setSelectedOrderForModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [cancelConfirmOrder, setCancelConfirmOrder] = useState(null);

  // Restaurant payment methods
  const [paymentMethods, setPaymentMethods] = useState(null);

  // Waiter view state
  const [selectedTable, setSelectedTable] = useState(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);

  // View mode hook
  const { viewMode, canToggle, setViewMode, loading: viewLoading } = useViewMode();

  // Tables hook (only fetches when in waiter mode)
  const { tables, groups, refreshTables } = useTables(
    viewMode === 'waiter' ? selectedRestaurant : null
  );

  // Waiter notifications (WAITER_CALL, BILL_REQUEST)
  const { alerts, clearAlert } = useWaiterNotifications(viewMode === 'waiter');

  // Sound hook
  const { preloadSounds, playNewOrderSound, playStatusChangeSound } = useStatusSounds();

  // Preload sounds on mount
  useEffect(() => {
    preloadSounds();
  }, [preloadSounds]);

  // Load orders function (defined before effects that use it)
  const loadOrders = useCallback(async () => {
    try {
      const restaurantId = storage.get('selectedRestaurantId');
      if (!restaurantId) {
        console.log(t('orders.noRestaurantSelected'));
        setLoading(false);
        return;
      }

      // Only load today's orders for comanda
      const today = new Date().toISOString().split('T')[0];
      const area = (viewMode === 'kitchen' || viewMode === 'bar') && !showAllAreas
        ? viewMode === 'bar' ? 'bar' : 'kitchen'
        : undefined;
      const response = await getOrders(null, today, { showAllAreas, area });
      setOrders(response.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      if (error.message === 'No hay restaurante seleccionado') {
        setOrders([]);
      }
    } finally {
      setLoading(false);
    }
  }, [showAllAreas, viewMode, t]);

  // WebSocket handlers (defined before effects that register them)
  const handleNewOrder = useCallback(
    (data) => {
      console.log('New order received:', data);
      playNewOrderSound();
      loadOrders();
    },
    [playNewOrderSound, loadOrders]
  );

  const handleOrderUpdated = useCallback((data) => {
    console.log('Order updated:', data);
    loadOrders();
  }, [loadOrders]);

  const handleCoordinationUpdate = useCallback((data) => {
    console.log('Coordination update:', data);
    const update = data.data || data;
    setOrders((prevOrders) =>
      prevOrders.map((o) =>
        o.orderId === update.orderId
          ? {
              ...o,
              kitchenStatus: update.kitchenStatus ?? o.kitchenStatus,
              barStatus: update.barStatus ?? o.barStatus,
            }
          : o
      )
    );
  }, []);

  // Delivery-specific WebSocket handlers
  const handleDeliveryStatusUpdate = useCallback(
    (data) => {
      console.log('Delivery status update received:', data);
      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.orderId === data.data?.orderId
            ? {
                ...o,
                deliveryStatus: data.data.newStatus,
                riderInfo: data.data.riderInfo || o.riderInfo,
              }
            : o
        )
      );
      if (data.data?.previousStatus && data.data?.newStatus) {
        playStatusChangeSound(data.data.previousStatus, data.data.newStatus);
      }
    },
    [playStatusChangeSound]
  );

  const handleDeliveryOrderCancelled = useCallback(
    (data) => {
      console.log('Delivery order cancelled:', data);
      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.orderId === data.data?.orderId
            ? {
                ...o,
                status: 'cancelled',
                deliveryStatus: 'cancelled',
                cancellationReason: data.data.reason,
              }
            : o
        )
      );
      playStatusChangeSound('preparing', 'cancelled');
    },
    [playStatusChangeSound]
  );

  // Load restaurant config (payment methods)
  useEffect(() => {
    const loadRestaurantConfig = async () => {
      try {
        const data = await getRestaurant();
        setPaymentMethods(data.paymentMethods || { cash: { enabled: true } });
      } catch (error) {
        console.error('Error loading restaurant config:', error);
        // Fallback: cash + card always available
        setPaymentMethods({ cash: { enabled: true }, card: { enabled: true } });
      }
    };
    const restaurantId = storage.get('selectedRestaurantId');
    if (restaurantId) {
      loadRestaurantConfig();
    }
  }, [selectedRestaurant]);

  // Load orders and setup WebSocket
  useEffect(() => {
    const restaurantId = storage.get('selectedRestaurantId');
    setSelectedRestaurant(restaurantId);

    loadOrders();

    // Connect WebSocket
    if (restaurantId) {
      websocket.connect(restaurantId);
      websocket.on('NEW_ORDER', handleNewOrder);
      websocket.on('ORDER_UPDATED', handleOrderUpdated);
      websocket.on('COORDINATION_UPDATE', handleCoordinationUpdate);
      websocket.on('DELIVERY_STATUS_UPDATE', handleDeliveryStatusUpdate);
      websocket.on('DELIVERY_ORDER_CANCELLED', handleDeliveryOrderCancelled);
    }

    return () => {
      websocket.off('NEW_ORDER', handleNewOrder);
      websocket.off('ORDER_UPDATED', handleOrderUpdated);
      websocket.off('COORDINATION_UPDATE', handleCoordinationUpdate);
      websocket.off('DELIVERY_STATUS_UPDATE', handleDeliveryStatusUpdate);
      websocket.off('DELIVERY_ORDER_CANCELLED', handleDeliveryOrderCancelled);
      websocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect restaurant change
  useEffect(() => {
    const checkRestaurantChange = () => {
      const currentRestaurantId = storage.get('selectedRestaurantId');

      if (currentRestaurantId !== selectedRestaurant) {
        console.log('Restaurant changed, reconnecting WebSocket...');
        setSelectedRestaurant(currentRestaurantId);
        loadOrders();

        if (currentRestaurantId) {
          websocket.connect(currentRestaurantId);
        }
      }
    };

    const interval = setInterval(checkRestaurantChange, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant, loadOrders]);

  // Reload when showAllAreas changes
  useEffect(() => {
    loadOrders();
  }, [showAllAreas, loadOrders]);

  // Auto-refresh orders every 30 seconds
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      loadOrders();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [loadOrders]);

  // Also refresh tables when orders refresh in waiter mode
  useEffect(() => {
    if (viewMode === 'waiter' && selectedRestaurant) {
      const interval = setInterval(() => {
        refreshTables();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [viewMode, selectedRestaurant, refreshTables]);

  const handleStatusChange = async (orderId, newStatus) => {
    const order = orders.find((o) => o.orderId === orderId);
    const previousStatus = order?.status;

    try {
      setOrders((prevOrders) =>
        prevOrders.map((o) => (o.orderId === orderId ? { ...o, status: newStatus } : o))
      );

      playStatusChangeSound(previousStatus, newStatus);
      await updateOrderStatus(orderId, newStatus);
      showToast(t('toast.orderStatusChanged', { status: t(`orders.status.${newStatus}`) }));
      if (newStatus === 'preparing') {
        trackEvent('order_moved_to_cooking', {
          restaurant_id: order?.restaurantId,
          order_id: orderId,
          items_count: order?.items?.length,
          table_number: order?.tableNumber,
        });
      } else if (newStatus === 'delivered') {
        trackEvent('order_delivered', {
          restaurant_id: order?.restaurantId,
          order_id: orderId,
          items_count: order?.items?.length,
          table_number: order?.tableNumber,
          order_total: order?.total,
        });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showToast(t('orders.updateError'), 'error');
      setOrders((prevOrders) =>
        prevOrders.map((o) => (o.orderId === orderId ? { ...o, status: previousStatus } : o))
      );
    }
  };

  const handleMarkPaid = async (order, method) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await updateOrder(order.orderId, {
        restaurantId: order.restaurantId,
        date: order.PK?.split('#')[2] || today,
        paymentStatus: 'paid',
        paymentMethod: method,
      });
      setIsModalOpen(false);
      setSelectedOrderForModal(null);
      loadOrders();
      showToast(t('toast.orderPaid'));
    } catch (error) {
      console.error('Error marking as paid:', error);
      showToast(t('orders.updateError'), 'error');
    }
  };

  const handleCardClick = (order) => {
    setSelectedOrderForModal(order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderForModal(null);
  };

  const handleEditOrder = (order) => {
    setIsModalOpen(false);
    setSelectedOrderForModal(null);
    setEditingOrder(order);
  };

  const handleSaveEditedOrder = async (order, updatedItems) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await updateOrder(order.orderId, {
        restaurantId: order.restaurantId,
        date: order.PK?.split('#')[2] || today,
        items: updatedItems,
      });
      loadOrders();
      showToast(t('toast.orderEdited'));
    } catch (error) {
      console.error('Error updating order:', error);
      showToast(t('orders.updateError'), 'error');
      throw error;
    }
  };

  const handleCancelOrder = (order) => {
    setIsModalOpen(false);
    setSelectedOrderForModal(null);
    setCancelConfirmOrder(order);
  };

  const executeCancelOrder = async () => {
    if (!cancelConfirmOrder) return;
    try {
      await updateOrderStatus(cancelConfirmOrder.orderId, 'cancelled');
      trackEvent('order_cancelled', {
        restaurant_id: cancelConfirmOrder.restaurantId,
        order_id: cancelConfirmOrder.orderId,
        items_count: cancelConfirmOrder.items?.length,
        table_number: cancelConfirmOrder.tableNumber,
      });
      loadOrders();
      showToast(t('toast.orderCancelled'));
    } catch (error) {
      console.error('Error cancelling order:', error);
      showToast(t('orders.updateError'), 'error');
    } finally {
      setCancelConfirmOrder(null);
    }
  };

  // Compute group tables for the selected table
  const selectedTableGroup = useMemo(() => {
    if (!selectedTable?.groupId || !groups.length) return [];
    const group = groups.find((g) => g.groupId === selectedTable.groupId);
    return group?.tableNumbers || [];
  }, [selectedTable, groups]);

  // Waiter view handlers
  const handleTableClick = (table) => {
    setSelectedTable(table);
    setIsDetailPanelOpen(true);
    clearAlert(String(table.tableNumber));
  };

  const handleCloseDetailPanel = () => {
    setIsDetailPanelOpen(false);
    setSelectedTable(null);
  };

  const handleTableAction = () => {
    loadOrders();
    refreshTables();
  };

  if (viewLoading) {
    return (
      <div className="orders-page">
        <Header
          viewMode={viewMode}
          canToggle={canToggle}
          onSetViewMode={setViewMode}
          showAllAreas={showAllAreas}
          onToggleAllAreas={() => setShowAllAreas((prev) => !prev)}
          canShowAllAreas={isAdmin()}
          userRole={role}
        />
        <main className="loading-container">
          <div className="loading-spinner" />
          <span>{t('common.loading')}</span>
        </main>
      </div>
    );
  }

  const restaurantId = storage.get('selectedRestaurantId');

  return (
    <div className="orders-page orders-page-kanban">
      <Header
        viewMode={viewMode}
        canToggle={canToggle}
        onSetViewMode={setViewMode}
        showAllAreas={showAllAreas}
        onToggleAllAreas={() => setShowAllAreas((prev) => !prev)}
        canShowAllAreas={isAdmin()}
        userRole={role}
      />

      <main className="orders-main-content">
        {!restaurantId ? (
          <div className="no-restaurant-selected">
            <span className="material-icons" aria-hidden="true">store</span>
            <h2>{t('orders.noRestaurantSelected')}</h2>
            <p>{t('orders.pleaseSelectRestaurant')}</p>
          </div>
        ) : viewMode === 'waiter' ? (
          <TableGrid
            tables={tables}
            orders={orders}
            alerts={alerts}
            groups={groups}
            onTableClick={handleTableClick}
            loading={loading}
          />
        ) : loading ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <span>{t('common.loading')}</span>
          </div>
        ) : (
          <KanbanBoard
            orders={orders}
            onStatusChange={handleStatusChange}
            onCardClick={handleCardClick}
            onCancelOrder={handleCancelOrder}
            activeOrder={activeOrder}
            setActiveOrder={setActiveOrder}
            userRole={role}
          />
        )}
      </main>

      {viewMode === 'waiter' && (
        <TableDetailPanel
          table={selectedTable}
          orders={orders}
          isOpen={isDetailPanelOpen}
          onClose={handleCloseDetailPanel}
          onStatusChange={handleStatusChange}
          onMarkPaid={handleMarkPaid}
          onEditOrder={handleEditOrder}
          onCancelOrder={handleCancelOrder}
          onTableAction={handleTableAction}
          groupTables={selectedTableGroup}
          paymentMethods={paymentMethods}
        />
      )}

      <OrderDetailModal
        order={selectedOrderForModal}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onMarkPaid={handleMarkPaid}
        onEditOrder={handleEditOrder}
        onCancelOrder={handleCancelOrder}
        paymentMethods={paymentMethods}
      />

      <EditOrderModal
        order={editingOrder}
        isOpen={!!editingOrder}
        onClose={() => setEditingOrder(null)}
        onSave={handleSaveEditedOrder}
      />

      {cancelConfirmOrder && (
        <ConfirmActionModal
          title={t('orders.cancelOrder')}
          message={t('orders.confirmCancel')}
          details={[
            { label: t('orders.orderNumber'), value: `#${cancelConfirmOrder.orderId.slice(-6)}` },
            { label: t('orders.items'), value: String(cancelConfirmOrder.items?.length || 0) },
          ]}
          confirmLabel={t('orders.cancelOrder')}
          variant="danger"
          onConfirm={executeCancelOrder}
          onCancel={() => setCancelConfirmOrder(null)}
        />
      )}
    </div>
  );
}

export default OrdersPage;
